import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBack
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      
      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to send password reset email. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
          <CheckCircle2 className="h-6 w-6 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold text-white">Check Your Email</h3>
        <p className="text-dark-800">
          We've sent a password reset link to <span className="text-white font-medium">{email}</span>. 
          Please check your inbox and follow the instructions.
        </p>
        <Button
          onClick={onBack}
          className="w-full bg-gold-400 hover:bg-gold-500 text-dark-50"
        >
          Return to Sign In
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={onBack}
          className="p-2 -ml-2 text-dark-800 hover:text-white transition-colors rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold text-white ml-2">Reset Password</h3>
      </div>

      <p className="text-dark-800">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm text-dark-800">
          Email Address
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dark-800" />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              "pl-10 bg-dark-300 border-dark-400 text-white h-11",
              error && "border-red-500"
            )}
            placeholder="name@example.com"
            required
          />
        </div>
        {error && (
          <div className="text-red-500 text-sm">{error}</div>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || !email.trim()}
        className="w-full bg-gold-400 hover:bg-gold-500 text-dark-50 h-11 font-medium"
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Sending...</span>
          </div>
        ) : (
          'Send Reset Link'
        )}
      </Button>
    </form>
  );
};